import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import {
  Description,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from './common/Catalyst/fieldset';
import { Input } from './common/Catalyst/input';
import { Select } from './common/Catalyst/select';
import { Textarea } from './common/Catalyst/textarea';
import { Button } from './common/Catalyst/button';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import ShowAlert from './ShowAlert';
import { bankService } from './clients/ApiClients';
import { NsfItemReturn } from './clients/rust-api-client';
import { formatCurrency, getBankErrorParts } from './utils/bankHelpers';
import { describeCutoff, nsfItemLabel } from './utils/nsfHelpers';

export type NsfDecisionMode = 'pay' | 'return';

interface NsfDecisionDialogProps {
  /** The item being decided; `null` closes the dialog. */
  item: NsfItemReturn | null;
  mode: NsfDecisionMode;
  connectionId: string;
  onClose: () => void;
  /** The item as the API returned it after the decision — landed or not. */
  onDecided: (after: NsfItemReturn) => void;
}

/**
 * The bank's `NsfRequestReason` enum: how the account will be funded to
 * cover a paid item. Spelled as the bank spells it.
 */
const REASONS = ['Wire', 'ACH', 'RDC', 'Book Transfer', 'Walk in deposit'];

/**
 * Pay or return one NSF item. **One-shot**: a decision is final through
 * the bank's API — changing it is a phone call to bank support, and only
 * before 1pm Central on the expiry date — so the dialog asks for a typed
 * confirmation and offers no withdraw. Pay is the only decision that
 * moves money (an undecided item is returned by default), so it also
 * requires how the funds are covered.
 */
const NsfDecisionDialog: React.FC<NsfDecisionDialogProps> = ({
  item,
  mode,
  connectionId,
  onClose,
  onDecided,
}) => {
  const [reason, setReason] = useState(REASONS[0]);
  const [fundingDetails, setFundingDetails] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<NsfItemReturn | null>(null);

  useEffect(() => {
    if (item) {
      setReason(REASONS[0]);
      setFundingDetails('');
      setConfirm('');
      setError(null);
      setResult(null);
    }
  }, [item, mode]);

  const word = mode === 'pay' ? 'PAY' : 'RETURN';
  const cutoff = useMemo(
    () => (item ? describeCutoff(item.decision_cutoff_at) : null),
    [item]
  );
  const amount = item?.amount ? formatCurrency(Number(item.amount)) : '—';

  const canSubmit =
    !!item &&
    !submitting &&
    confirm.trim() === word &&
    (mode === 'return' || fundingDetails.trim().length > 0);

  const handleSubmit = async () => {
    if (!item) return;
    if (mode === 'pay' && !fundingDetails.trim()) {
      setError('Say how the account will be funded to cover this item.');
      return;
    }
    if (confirm.trim() !== word) {
      setError(`Type ${word} to confirm.`);
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const resp =
        mode === 'pay'
          ? await bankService.payConnectionNsf({
              connectionId,
              id: item.id,
              nsfPayDecision: {
                reason,
                funding_details: fundingDetails.trim(),
              },
            })
          : await bankService.returnConnectionNsf({
              connectionId,
              id: item.id,
              nsfReturnDecision: {
                funding_details: fundingDetails.trim() || null,
              },
            });
      setResult(resp.data);
      onDecided(resp.data);
    } catch (err) {
      const { body } = getBankErrorParts(err);
      setError(body || `Couldn't ${mode} the item.`);
    } finally {
      setSubmitting(false);
    }
  };

  const landed =
    result &&
    (result.decision === 'pay_requested' ||
      result.decision === 'return_requested');

  return (
    <Dialog open={!!item} onClose={submitting ? () => undefined : onClose}>
      <DialogTitle>
        {mode === 'pay' ? 'Pay' : 'Return'} {item ? nsfItemLabel(item) : ''}
      </DialogTitle>
      <DialogDescription>
        {amount} presented against account {item?.account_number ?? '—'}.{' '}
        {mode === 'pay'
          ? 'Paying overdraws the account to honour it. The bank will likely ask how the funds are covered.'
          : 'The bank returns it unpaid — which is also what happens if nobody decides. This makes the decision explicit and attributable.'}
      </DialogDescription>
      <DialogBody>
        {error && (
          <div className="mb-4">
            <ShowAlert
              alertType="error"
              alertTitle={`Cannot ${mode}`}
              alertMessage={error}
              isVisible={!!error}
              onClose={() => setError(null)}
              timeout={15000}
              isInline={true}
            />
          </div>
        )}

        {result ? (
          <div className="flex flex-col gap-3">
            {landed ? (
              <>
                <div className="flex items-center gap-2">
                  <Badge color={mode === 'pay' ? 'sky' : 'purple'}>
                    {mode === 'pay' ? 'Pay requested' : 'Return requested'}
                  </Badge>
                  {result.bank_request_status && (
                    <Text className="text-sm">
                      Bank: {result.bank_request_status}
                    </Text>
                  )}
                </div>
                <Text className="text-sm">
                  The bank has the request.{' '}
                  <strong>It cannot be changed here.</strong> To reverse it,
                  call Columbia Bank support — before{' '}
                  {cutoff?.central ?? 'the cutoff'}; after that the outcome is
                  fixed.
                </Text>
              </>
            ) : result.decision === 'decided_at_bank' ? (
              <Text className="text-sm">
                The bank already holds a{' '}
                <strong>{result.bank_request_type}</strong> request for this
                item, made in Online Banking or by the bank's default. Your
                request was not placed.
              </Text>
            ) : (
              <>
                <Badge color="amber">Not decided</Badge>
                <Text className="text-sm">
                  The bank did not take the request:{' '}
                  {result.bank_request_error_message ?? 'no reason given'}
                  {result.bank_request_error_code
                    ? ` (code ${result.bank_request_error_code})`
                    : ''}
                  . The item is still open; check Online Banking before trying
                  again.
                </Text>
              </>
            )}
          </div>
        ) : (
          <Fieldset>
            <FieldGroup>
              {cutoff && (
                <Text className="text-sm">
                  Deadline <strong>{cutoff.central}</strong>
                  {cutoff.local ? ` (${cutoff.local} your time)` : ''}
                  {cutoff.remaining ? ` · ${cutoff.remaining}` : ''}
                </Text>
              )}
              {mode === 'pay' && (
                <Field>
                  <Label>How the account will be funded</Label>
                  <Select
                    name="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  >
                    {REASONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <Field>
                <Label>
                  {mode === 'pay' ? 'Funding details' : 'Note (optional)'}
                </Label>
                <Textarea
                  name="funding_details"
                  rows={3}
                  value={fundingDetails}
                  onChange={(e) => setFundingDetails(e.target.value)}
                  placeholder={
                    mode === 'pay'
                      ? 'e.g. Wire of $5,000 from the reserve account this afternoon'
                      : 'e.g. Owner says the check was a duplicate'
                  }
                />
                {mode === 'pay' && (
                  <Description>
                    Required. The bank may ask for evidence, such as another
                    account's balance.
                  </Description>
                )}
              </Field>
              <Field>
                <Label>Type {word} to confirm</Label>
                <Input
                  name="confirm"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="off"
                  placeholder={word}
                />
                <Description>
                  This cannot be undone here. Changing a decision afterwards is
                  a phone call to Columbia Bank support, before the deadline.
                </Description>
              </Field>
            </FieldGroup>
          </Fieldset>
        )}
      </DialogBody>
      <DialogActions>
        {result ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button plain onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              color={mode === 'pay' ? 'sky' : 'red'}
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting
                ? mode === 'pay'
                  ? 'Requesting pay…'
                  : 'Requesting return…'
                : mode === 'pay'
                  ? 'Pay this item'
                  : 'Return this item'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NsfDecisionDialog;
